import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    TextField,
    Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import webTokenStorer from '../../../../utils/webStorer';
import { notify } from '../../../../utils/notification';
import { updateUserReq, setUserStatusReq, userErrorMessage } from '../../../../axios/request/SocietyController/CtrlUserReqs/CtrlUserReqs';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

const EMPTY_FORM = {
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    confirm_password: '',
    status: 'active',
};

const labelCls = 'tw-block tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-mb-1';

const EditAdminUserModal = ({ open, onClose, admin, onSaved }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const raw = admin?.raw || {};
    const userId = admin?.id;
    // The API doesn't stop a super admin deactivating themselves.
    const isSelf = userId != null && String(webTokenStorer.getSessionUserId()) === String(userId);

    useEffect(() => {
        if (admin && open) {
            setFormData({
                ...EMPTY_FORM,
                first_name: raw.first_name || '',
                last_name: raw.last_name || '',
                phone: raw.phone || '',
                status: raw.is_active ? 'active' : 'inactive',
            });
            setErrors({});
            setServerError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admin, open]);

    const setField = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validate = () => {
        const e = {};
        const firstName = formData.first_name.trim();
        if (!firstName) e.first_name = 'First name is required';
        else if (firstName.length > 100) e.first_name = 'At most 100 characters';
        if (formData.last_name.trim().length > 100) e.last_name = 'At most 100 characters';
        if (formData.phone.trim().length > 20) e.phone = 'At most 20 characters';
        if (formData.password || formData.confirm_password) {
            if (formData.password.length < 8 || formData.password.length > 72) e.password = 'Password must be 8–72 characters';
            else if (formData.password !== formData.confirm_password) e.confirm_password = 'Passwords do not match';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        setServerError('');
        if (!validate()) return;

        // Partial update: only changed fields, trimmed (the server stores values as sent).
        const body = {};
        const firstName = formData.first_name.trim();
        const lastName = formData.last_name.trim();
        const phone = formData.phone.trim();
        if (firstName !== (raw.first_name || '')) body.first_name = firstName;
        if (lastName !== (raw.last_name || '')) body.last_name = lastName; // "" clears it
        if (phone !== (raw.phone || '')) body.phone = phone;               // "" clears it
        if (formData.password) body.password = formData.password;

        // Status goes through PATCH /users/:id/status (revokes sessions on
        // deactivate), not through the profile PATCH.
        const wantActive = formData.status === 'active' ? 1 : 0;
        let statusChange = null;
        if (!isSelf && wantActive !== (raw.is_active ? 1 : 0)) statusChange = wantActive;

        if (Object.keys(body).length === 0 && statusChange === null) {
            notify('info', 'No changes to save');
            return;
        }

        setIsLoading(true);
        try {
            let updated = null;
            if (Object.keys(body).length > 0) {
                const res = await updateUserReq(userId, body);
                if (res?.success === false) throw new Error(res?.message);
                updated = res?.data;
            }
            if (statusChange !== null) {
                try {
                    const res = await setUserStatusReq(userId, statusChange);
                    if (res?.success === false) throw new Error(res?.message);
                    updated = res?.data || updated;
                } catch (err) {
                    // 409 = already in that state — the outcome the admin asked for.
                    if (err?.response?.status !== 409) throw err;
                }
            }

            notify('success', statusChange === 0
                ? 'User deactivated'
                : statusChange === 1 ? 'User activated' : 'User updated successfully');
            onSaved?.(updated);
            onClose();
        } catch (err) {
            const message = userErrorMessage(err);
            setServerError(message);
            notify('error', message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxWidth: 520 } }}
        >
            {/* Header — a div, not DialogTitle's <h2>, which inherits the theme's large heading size */}
            <DialogTitle component="div" sx={{ p: 0 }}>
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
                }}>
                    <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Edit Admin User</div>
                    <IconButton aria-label="close" onClick={onClose} size="small" disabled={isLoading} sx={{ color: 'text.secondary', p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    px: 2,
                    pb: 2,
                    '&.MuiDialogContent-root': { paddingTop: '12px' },
                    // Compact fields to match the rest of the controller screens
                    '& .MuiInputLabel-root': { fontSize: '13px', transform: 'translate(12px, 8px) scale(1)' },
                    '& .MuiInputLabel-root.MuiInputLabel-shrink': { transform: 'translate(14px, -7px) scale(0.8)' },
                    '& .MuiInputBase-input': { fontSize: '13px', padding: '7px 12px' },
                    '& .MuiFormHelperText-root': { fontSize: '11px', mx: 0.5 },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e11d48' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e11d48' },
                }}
            >
                {/* Email can't be changed by this endpoint — show it as text */}
                <div className="tw-mb-3 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-bg-gray-50 tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                    <Icon icon="solar:letter-bold" className="tw-text-rose-500 tw-text-xs tw-flex-shrink-0" />
                    <span className="tw-text-xs tw-text-gray-700 tw-truncate">{raw.email || admin?.email || '—'}</span>
                    <span className="tw-ml-auto tw-text-[10px] tw-text-gray-400 tw-flex-shrink-0">read-only</span>
                </div>

                {serverError && (
                    <div className="tw-mb-3 tw-flex tw-items-start tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-bg-red-50 tw-border tw-border-red-100 tw-text-[11px] tw-text-red-700">
                        <Icon icon="mdi:alert-circle-outline" className="tw-text-sm tw-flex-shrink-0 tw-mt-px" />
                        {serverError}
                    </div>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 2, mt: 1 }}>
                    <TextField
                        fullWidth label="First Name" required size="small"
                        value={formData.first_name}
                        onChange={(e) => setField('first_name', e.target.value)}
                        error={Boolean(errors.first_name)} helperText={errors.first_name}
                        inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                        fullWidth label="Last Name" size="small"
                        value={formData.last_name}
                        onChange={(e) => setField('last_name', e.target.value)}
                        error={Boolean(errors.last_name)} helperText={errors.last_name}
                        inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                        fullWidth label="Phone" size="small"
                        value={formData.phone}
                        onChange={(e) => setField('phone', e.target.value)}
                        error={Boolean(errors.phone)} helperText={errors.phone}
                        inputProps={{ maxLength: 20 }}
                    />
                    <Box />

                    <Box>
                        <label className={labelCls}>Status</label>
                        <SearchableDropdown
                            value={formData.status}
                            onChange={(v) => setField('status', v)}
                            options={STATUS_OPTIONS}
                            placeholder="Status"
                            clearable={false}
                            disabled={isSelf}
                            small
                        />
                    </Box>
                </Box>

                {isSelf ? (
                    <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1.5 tw-mb-0">
                        You can't change your own status — that could lock you out.
                    </p>
                ) : formData.status === 'inactive' && raw.is_active ? (
                    <p className="tw-text-[11px] tw-text-amber-700 tw-mt-1.5 tw-mb-0">
                        Deactivating logs this user out everywhere and blocks login. Their society roles are kept.
                    </p>
                ) : null}

                {/* Password reset — optional; the backend has no confirm field, so check it here */}
                <div className="tw-mt-4 tw-pt-3 tw-border-t tw-border-gray-100">
                    <div className="tw-text-xs tw-font-semibold tw-text-gray-700">Reset Password</div>
                    <p className="tw-text-[11px] tw-text-gray-400 tw-mb-2">Leave blank to keep the current password.</p>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            fullWidth label="New Password" type="password" size="small" autoComplete="new-password"
                            value={formData.password}
                            onChange={(e) => setField('password', e.target.value)}
                            error={Boolean(errors.password)} helperText={errors.password}
                            inputProps={{ maxLength: 72 }}
                        />
                        <TextField
                            fullWidth label="Confirm Password" type="password" size="small" autoComplete="new-password"
                            value={formData.confirm_password}
                            onChange={(e) => setField('confirm_password', e.target.value)}
                            error={Boolean(errors.confirm_password)} helperText={errors.confirm_password}
                            inputProps={{ maxLength: 72 }}
                        />
                    </Box>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                <button
                    type="button"
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-60"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    style={{ marginLeft: 0 }}
                >
                    {isLoading ? (
                        <>
                            <span className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />
                            <span>Saving…</span>
                        </>
                    ) : (
                        <>
                            <Icon icon="mdi:check" className="tw-text-sm" />
                            <span>Save Changes</span>
                        </>
                    )}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default EditAdminUserModal;
